import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import settingsService from '../../services/settingsService';
import ThemeSelector from '../../components/ThemeSelector';
import { useTheme } from '../../contexts/ThemeContext';
import LogoStore from './LogoStore';

const Settings = () => {
  const navigate = useNavigate();
  const { currentTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  })

  const setSelectedLogo = LogoStore(
    (state) => state.setSelectedLogo
  );


  const handlePasswordToggle = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Profile state
  const [profile, setProfile] = useState({
    id: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    bio: '',
    avatar: ''
  });

  // Password state
  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Organization state
  const [organization, setOrganization] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',
    logo_url: ''
  });

  // Logo upload state
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Preferences state
  const [preferences, setPreferences] = useState({
    timezone: 'UTC',
    language: 'en',
    date_format: 'MM/DD/YYYY',
    time_format: '12h',
    theme: 'light',
    notifications_enabled: true,
    email_notifications: true
  });

  // Security state
  const [security, setSecurity] = useState({
    two_factor_enabled: false,
    last_password_change: null,
    account_created: null
  });

  useEffect(() => {
    loadData();
  }, [activeTab, currentTheme]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'profile') {
        const data = await settingsService.getUserProfile();
        setProfile({
          id: data.id,
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          email: data.email || '',
          phone: data.phone || '',
          bio: data.bio || '',
          avatar: data.avatar_url || ''
        });
        setAvatarPreview(data.avatar_url);
        setSelectedLogo({
          ...data,
          avatar_url: data.avatar_url,
        });
      } else if (activeTab === 'organization') {
        const data = await settingsService.getOrganizationSettings();
        setOrganization({
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || '',
          website: data.website || '',
          address: data.address || '',
          city: data.city || '',
          state: data.state || '',
          country: data.country || '',
          postal_code: data.postal_code || '',
          logo_url: data.logo_url || ''
        });
        // setLogoPreview(data.logo_url ? `${process.env.REACT_APP_API_URL || 'http://localhost:5001'}${data.logo_url}` : null);
        setLogoPreview(data.logo_url);
      } else if (activeTab === 'preferences') {
        const data = await settingsService.getUserPreferences();
        // Sync with current theme from ThemeContext
        setPreferences({
          ...data,
          theme: currentTheme
        });
      } else if (activeTab === 'security') {
        const data = await settingsService.getSecuritySettings();
        setSecurity(data);
      }
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await settingsService.updateUserProfile(profile);
      showMessage('success', 'Profile updated successfully');
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await settingsService.changePassword(passwords);
      showMessage('success', 'Password changed successfully');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleOrganizationSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await settingsService.updateOrganizationSettings(organization);
      showMessage('success', 'Organization settings updated successfully');
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Failed to update organization settings');
    } finally {
      setLoading(false);
    }
  };

  const handlePreferencesSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await settingsService.updateUserPreferences(preferences);
      showMessage('success', 'Preferences updated successfully');
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Failed to update preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTwoFactor = async () => {
    try {
      setLoading(true);
      const newValue = !security.two_factor_enabled;
      await settingsService.toggleTwoFactor(newValue);
      setSecurity({ ...security, two_factor_enabled: newValue });
      showMessage('success', `Two-factor authentication ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Failed to toggle two-factor authentication');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showMessage('error', 'Please select an image file');
        return;
      }
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        showMessage('error', 'File size must be less than 5MB');
        return;
      }
      setLogoFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) {
      showMessage('error', 'Please select a logo file');
      return;
    }
    try {
      setUploadingLogo(true);
      const response = await settingsService.uploadOrganizationLogo(logoFile);
      setOrganization({ ...organization, logo_url: response.logo_url });
      setLogoFile(null);
      setLogoPreview(null); // Clear preview to show actual uploaded logo
      showMessage('success', 'Logo uploaded successfully');
      await loadData(); // Reload to get updated logo
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the organization logo?')) {
      return;
    }
    try {
      setLoading(true);
      await settingsService.deleteOrganizationLogo();
      setOrganization({ ...organization, logo_url: '' });
      setLogoPreview(null);
      setLogoFile(null);
      showMessage('success', 'Logo deleted successfully');
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Failed to delete logo');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showMessage('error', 'Please select an image file');
        return;
      }
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        showMessage('error', 'File size must be less than 5MB');
        return;
      }
      setAvatarFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAvatarUpload = async () => {
    if (!avatarFile) {
      showMessage('error', 'Please select a logo file');
      return;
    }
    try {
      setUploadingAvatar(true);
      const response = await settingsService.uploadAvatarLogo(avatarFile, profile.id);
      setProfile({ ...profile, avatar: response.logo_url });
      setAvatarFile(null);
      setAvatarPreview(null); // Clear preview to show actual uploaded logo
      showMessage('success', 'Logo uploaded successfully');

      await loadData(); // Reload to get updated logo
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Failed to upload logo');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleAvatarDelete = async () => {
    // if (!window.confirm('Are you sure you want to delete the organization logo?')) {
    //   return;
    // }
    // try {
    //   setLoading(true);
    //   await settingsService.deleteOrganizationLogo();
    //   setOrganization({ ...organization, logo_url: '' });
    //   setLogoPreview(null);
    //   setLogoFile(null);
    //   showMessage('success', 'Logo deleted successfully');
    // } catch (error) {
    //   showMessage('error', error.response?.data?.error || 'Failed to delete logo');
    // } finally {
    //   setLoading(false);
    // }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: '👤' },
    { id: 'organization', label: 'Organization', icon: '🏢' },
    { id: 'preferences', label: 'Preferences', icon: '⚙️' },
    { id: 'security', label: 'Security', icon: '🔒' },
    { id: 'roles', label: 'Roles & Permissions', icon: '🛡️', link: '/app/settings/roles' }
  ];

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Settings</h1>

      {message.text && (
        <div className={`mb-4 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-800 custom-bg-green' : 'bg-red-100 text-red-800'}`}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => tab.link ? navigate(tab.link) : setActiveTab(tab.id)}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {loading && <div className="text-center py-8">Loading...</div>}

          {!loading && activeTab === 'profile' && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Profile Information</h2>
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-start gap-4">
                  {(avatarPreview || profile.avatar_url) && (
                    <div className="flex-shrink-0">
                      <img
                        src={avatarPreview || `http://localhost:5001${organization.logo_url}`}
                        alt="Organization Logo"
                        className="w-32 h-32 object-contain border border-gray-300 rounded-lg bg-white p-2"
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarFileChange}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 mb-2"
                    />
                    <p className="text-xs text-gray-500 mb-3">
                      Accepted formats: JPG, PNG, GIF, SVG, WEBP (Max 5MB)
                    </p>
                    <div className="flex gap-2">
                      {avatarFile && (
                        <button
                          type="button"
                          onClick={handleAvatarUpload}
                          disabled={uploadingAvatar}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
                        >
                          {uploadingAvatar ? 'Uploading...' : 'Upload Avatar'}
                        </button>
                      )}
                      {profile.avatar && (
                        <button
                          type="button"
                          onClick={handleAvatarDelete}
                          disabled={loading}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm"
                        >
                          Delete Avatar
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <input
                      type="text"
                      value={profile.first_name}
                      onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                      type="text"
                      value={profile.last_name}
                      onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-4'>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={profile.email}
                      onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={profile.phone}
                      onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                  <textarea
                    value={profile.bio}
                    onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Save Profile
                </button>
              </form>

              <hr className="my-8" />

              <h2 className="text-xl font-semibold mb-4">Change Password</h2>
              <form onSubmit={handlePasswordSubmit} className="space-y-4 max-w-md">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                  <div className="password-container">
                    <input
                      type={showPasswords.current ? "text" : "password"}
                      value={passwords.current_password}
                      onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                    <i
                      className={`fas ${showPasswords.current ? "fa-eye-slash" : "fa-eye"}`}
                      onClick={() => handlePasswordToggle("current")}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                  <div className="password-container">
                    <input
                      type={showPasswords.new ? "text" : "password"}
                      value={passwords.new_password}
                      onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      minLength="6"
                    />
                    <i
                      className={`fas ${showPasswords.new ? "fa-eye-slash" : "fa-eye"}`}
                      onClick={() => handlePasswordToggle("new")}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                  <div className="password-container">
                    <input
                      type={showPasswords.confirm ? "text" : "password"}
                      value={passwords.confirm}
                      onChange={(e) => setPasswords({ ...passwords, confirm_password: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      minLength="6"
                    />
                    <i
                      className={`fas ${showPasswords.confirm ? "fa-eye-slash" : "fa-eye"}`}
                      onClick={() => handlePasswordToggle("confirm")}
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Change Password
                </button>
              </form>
            </div>
          )}

          {!loading && activeTab === 'organization' && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Organization Settings</h2>

              {/* Logo Upload Section */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-medium mb-3">Organization Logo</h3>
                <div className="flex items-start gap-4">
                  {(logoPreview || organization.logo_url) && (
                    <div className="flex-shrink-0">
                      <img
                        src={logoPreview || `http://localhost:5001${organization.logo_url}`}
                        alt="Organization Logo"
                        className="w-32 h-32 object-contain border border-gray-300 rounded-lg bg-white p-2"
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoFileChange}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 mb-2"
                    />
                    <p className="text-xs text-gray-500 mb-3">
                      Accepted formats: JPG, PNG, GIF, SVG, WEBP (Max 5MB)
                    </p>
                    <div className="flex gap-2">
                      {logoFile && (
                        <button
                          type="button"
                          onClick={handleLogoUpload}
                          disabled={uploadingLogo}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
                        >
                          {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                        </button>
                      )}
                      {organization.logo_url && (
                        <button
                          type="button"
                          onClick={handleLogoDelete}
                          disabled={loading}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm"
                        >
                          Delete Logo
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <form onSubmit={handleOrganizationSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name</label>
                  <input
                    type="text"
                    value={organization.name}
                    onChange={(e) => setOrganization({ ...organization, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={organization.email}
                      onChange={(e) => setOrganization({ ...organization, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={organization.phone}
                      onChange={(e) => setOrganization({ ...organization, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <input
                    type="url"
                    value={organization.website}
                    onChange={(e) => setOrganization({ ...organization, website: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    value={organization.address}
                    onChange={(e) => setOrganization({ ...organization, address: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={organization.city}
                      onChange={(e) => setOrganization({ ...organization, city: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <input
                      type="text"
                      value={organization.state}
                      onChange={(e) => setOrganization({ ...organization, state: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                    <input
                      type="text"
                      value={organization.postal_code}
                      onChange={(e) => setOrganization({ ...organization, postal_code: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <input
                    type="text"
                    value={organization.country}
                    onChange={(e) => setOrganization({ ...organization, country: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Save Organization Settings
                </button>
              </form>
            </div>
          )}

          {!loading && activeTab === 'preferences' && (
            <div>
              <h2 className="text-xl font-semibold mb-4">User Preferences</h2>
              <form onSubmit={handlePreferencesSubmit} className="space-y-4 max-w-2xl">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                    <select
                      value={preferences.timezone}
                      onChange={(e) => setPreferences({ ...preferences, timezone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">Eastern Time</option>
                      <option value="America/Chicago">Central Time</option>
                      <option value="America/Denver">Mountain Time</option>
                      <option value="America/Los_Angeles">Pacific Time</option>
                      <option value="Europe/London">London</option>
                      <option value="Asia/Calcutta">India</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                    <select
                      value={preferences.language}
                      onChange={(e) => setPreferences({ ...preferences, language: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                      <option value="de">German</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date Format</label>
                    <select
                      value={preferences.date_format}
                      onChange={(e) => setPreferences({ ...preferences, date_format: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Time Format</label>
                    <select
                      value={preferences.time_format}
                      onChange={(e) => setPreferences({ ...preferences, time_format: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="12h">12 Hour</option>
                      <option value="24h">24 Hour</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Theme</label>
                  <ThemeSelector />
                </div>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={preferences.notifications_enabled}
                      onChange={(e) => setPreferences({ ...preferences, notifications_enabled: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Enable notifications</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={preferences.email_notifications}
                      onChange={(e) => setPreferences({ ...preferences, email_notifications: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Enable email notifications</span>
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Save Preferences
                </button>
              </form>
            </div>
          )}

          {!loading && activeTab === 'security' && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Security Settings</h2>
              <div className="space-y-6 max-w-2xl">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">Two-Factor Authentication</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Add an extra layer of security to your account
                      </p>
                    </div>
                    <button
                      onClick={handleToggleTwoFactor}
                      disabled={loading}
                      className={`px-4 py-2 rounded-lg font-medium ${security.two_factor_enabled
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-green-600 text-white hover:bg-green-700'
                        } disabled:opacity-50`}
                    >
                      {security.two_factor_enabled ? 'Disable' : 'Enable'}
                    </button>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-2">Account Information</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Last Password Change:</span>
                      <span className="text-gray-900">
                        {security.last_password_change
                          ? new Date(security.last_password_change).toLocaleDateString()
                          : 'Never'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Account Created:</span>
                      <span className="text-gray-900">
                        {security.account_created
                          ? new Date(security.account_created).toLocaleDateString()
                          : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;


